import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Save, Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PessoaSearchCombobox } from "@/components/PessoaSearchCombobox";
import { getApiErrorMessage } from "@/lib/apiClient";
import ProjetoCustosTab from "./ProjetoCustosTab";
import ProjetoFaturamentoTab from "./ProjetoFaturamentoTab";
import ProjetoGanttTab from "./ProjetoGanttTab";

import {
  useProjeto, useProjetoMutations,
  useFases, useTarefas, useDependencias, useProjetoSubMutations,
} from "@/features/projetos/hooks/useProjetos";
import { useUsuarios } from "@/features/usuarios/hooks/useUsuarios";
import { usePermissoes } from "@/contexts/PermissoesContext";
import { FORMULARIOS } from "@/domain/formularios";
import { STATUS_PROJETO, STATUS_TAREFA, TIPO_DEPENDENCIA } from "@/features/projetos/types";

const SEM_FASE = "0";
const onlyDate = (d?: string | null) => (d ? d.slice(0, 10) : "");
const orNull = (s: string) => (s ? s : null);

export default function ProjetoForm() {
  const { id } = useParams();
  const projetoId = id ? Number(id) : undefined;
  const isNew = !projetoId;
  const navigate = useNavigate();

  const { data: projeto } = useProjeto(projetoId);
  const { criar, atualizar } = useProjetoMutations();
  const { query: usuariosQuery } = useUsuarios();
  const usuarios = usuariosQuery.data?.itens ?? [];
  const { temPermissao } = usePermissoes();
  const podeFaturamento = temPermissao(FORMULARIOS.PROJETO_FATURAMENTO, "ler");

  // ── estado da aba Dados ────────────────────────────────────────────────
  const [nome, setNome] = useState("");
  const [clienteId, setClienteId] = useState(0);
  const [gerenteId, setGerenteId] = useState(0);
  const [status, setStatus] = useState(0);
  const [dtInicio, setDtInicio] = useState("");
  const [dtFim, setDtFim] = useState("");
  const [valorOrcado, setValorOrcado] = useState("0");
  const [observacao, setObservacao] = useState("");

  useEffect(() => {
    if (!projeto) return;
    setNome(projeto.nome);
    setClienteId(projeto.clienteId);
    setGerenteId(projeto.gerenteId);
    setStatus(projeto.status);
    setDtInicio(onlyDate(projeto.dtInicioPlanejada));
    setDtFim(onlyDate(projeto.dtFimPlanejada));
    setValorOrcado(String(projeto.valorOrcado ?? 0));
    setObservacao(projeto.observacao ?? "");
  }, [projeto]);

  const salvarDados = async () => {
    if (!nome.trim()) { toast.error("Informe o nome do projeto."); return; }
    const payload = {
      nome: nome.trim(), clienteId, gerenteId, status,
      dtInicioPlanejada: orNull(dtInicio), dtFimPlanejada: orNull(dtFim),
      valorOrcado: Number(valorOrcado) || 0, observacao: orNull(observacao),
    };
    try {
      if (isNew) {
        const criado = await criar.mutateAsync(payload);
        toast.success("Projeto criado. Agora gerencie fases, tarefas e dependências.");
        navigate(`/operacional/projetos/${criado.id}`, { replace: true });
      } else {
        await atualizar.mutateAsync({ id: projetoId!, req: payload });
        toast.success("Projeto salvo.");
      }
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Não foi possível salvar o projeto."));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate("/operacional/projetos")}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">{isNew ? "Novo Projeto" : projeto?.nome ?? "Projeto"}</h1>
            {!isNew && projeto && (
              <p className="text-sm text-muted-foreground">
                Progresso: <span className="font-semibold">{Number(projeto.percentualConcluido).toFixed(2)}%</span>
              </p>
            )}
          </div>
        </div>
        <Button onClick={salvarDados} disabled={criar.isPending || atualizar.isPending}>
          <Save className="mr-2 h-4 w-4" /> Salvar
        </Button>
      </div>

      <Tabs defaultValue="dados">
        <TabsList>
          <TabsTrigger value="dados">Dados</TabsTrigger>
          <TabsTrigger value="fases" disabled={isNew}>Fases</TabsTrigger>
          <TabsTrigger value="tarefas" disabled={isNew}>Tarefas</TabsTrigger>
          <TabsTrigger value="dependencias" disabled={isNew}>Dependências</TabsTrigger>
          <TabsTrigger value="custos" disabled={isNew}>Custos</TabsTrigger>
          {podeFaturamento && <TabsTrigger value="faturamento" disabled={isNew}>Faturamento</TabsTrigger>}
          <TabsTrigger value="gantt" disabled={isNew}>Gantt</TabsTrigger>
        </TabsList>

        {/* ── DADOS ── */}
        <TabsContent value="dados">
          <Card>
            <CardContent className="grid grid-cols-1 gap-4 pt-6 md:grid-cols-2">
              <div className="md:col-span-2">
                <Label>Nome *</Label>
                <Input value={nome} onChange={e => setNome(e.target.value)} maxLength={120} />
              </div>
              <div>
                <Label>Cliente</Label>
                <PessoaSearchCombobox value={clienteId || undefined} onChange={v => setClienteId(v)} filter="cliente" placeholder="Selecione o cliente..." />
              </div>
              <div>
                <Label>Gerente</Label>
                <Select value={String(gerenteId)} onValueChange={v => setGerenteId(Number(v))}>
                  <SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">— Nenhum —</SelectItem>
                    {usuarios.map(u => <SelectItem key={u.id} value={String(u.id)}>{u.nome}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Status</Label>
                <Select value={String(status)} onValueChange={v => setStatus(Number(v))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUS_PROJETO.map((s, i) => <SelectItem key={i} value={String(i)}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Valor orçado</Label>
                <Input type="number" step="0.01" value={valorOrcado} onChange={e => setValorOrcado(e.target.value)} />
              </div>
              <div>
                <Label>Início planejado</Label>
                <Input type="date" value={dtInicio} onChange={e => setDtInicio(e.target.value)} />
              </div>
              <div>
                <Label>Fim planejado</Label>
                <Input type="date" value={dtFim} onChange={e => setDtFim(e.target.value)} />
              </div>
              <div className="md:col-span-2">
                <Label>Observação</Label>
                <Textarea value={observacao} onChange={e => setObservacao(e.target.value)} rows={4} />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {!isNew && projetoId && (
          <>
            <TabsContent value="fases"><FasesTab projetoId={projetoId} /></TabsContent>
            <TabsContent value="tarefas"><TarefasTab projetoId={projetoId} usuarios={usuarios} /></TabsContent>
            <TabsContent value="dependencias"><DependenciasTab projetoId={projetoId} /></TabsContent>
            <TabsContent value="custos"><ProjetoCustosTab projetoId={projetoId} /></TabsContent>
            {podeFaturamento && <TabsContent value="faturamento"><ProjetoFaturamentoTab projetoId={projetoId} /></TabsContent>}
            <TabsContent value="gantt"><ProjetoGanttTab projetoId={projetoId} /></TabsContent>
          </>
        )}
      </Tabs>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  FASES
// ════════════════════════════════════════════════════════════════════════════
function FasesTab({ projetoId }: { projetoId: number }) {
  const { data: fases = [] } = useFases(projetoId);
  const { salvarFase, excluirFase } = useProjetoSubMutations(projetoId);

  const [editId, setEditId] = useState<number | null>(null);
  const [nome, setNome] = useState("");
  const [ordem, setOrdem] = useState("1");

  const limpar = () => { setEditId(null); setNome(""); setOrdem("1"); };

  const salvar = async () => {
    if (!nome.trim()) { toast.error("Informe o nome da fase."); return; }
    try {
      await salvarFase.mutateAsync({ faseId: editId ?? undefined, req: { nome: nome.trim(), ordem: Number(ordem) || 0 } });
      toast.success("Fase salva.");
      limpar();
    } catch (e) { toast.error(getApiErrorMessage(e, "Erro ao salvar fase.")); }
  };

  const remover = async (fid: number) => {
    try { await excluirFase.mutateAsync(fid); toast.success("Fase removida."); if (editId === fid) limpar(); }
    catch (e) { toast.error(getApiErrorMessage(e, "Erro ao remover fase.")); }
  };

  return (
    <Card>
      <CardHeader><CardTitle className="text-base">{editId ? "Editar fase" : "Nova fase"}</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[220px]"><Label>Nome</Label><Input value={nome} onChange={e => setNome(e.target.value)} maxLength={120} /></div>
          <div className="w-24"><Label>Ordem</Label><Input type="number" value={ordem} onChange={e => setOrdem(e.target.value)} /></div>
          <Button onClick={salvar}><Save className="mr-2 h-4 w-4" />{editId ? "Atualizar" : "Adicionar"}</Button>
          {editId && <Button variant="outline" onClick={limpar}>Cancelar</Button>}
        </div>
        <Table>
          <TableHeader><TableRow><TableHead>Fase</TableHead><TableHead className="w-20">Ordem</TableHead><TableHead className="w-32">% Concluído</TableHead><TableHead className="w-24 text-right">Ações</TableHead></TableRow></TableHeader>
          <TableBody>
            {fases.length === 0 ? (
              <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground">Nenhuma fase.</TableCell></TableRow>
            ) : fases.map(f => (
              <TableRow key={f.id}>
                <TableCell className="font-medium">{f.nome}</TableCell>
                <TableCell>{f.ordem}</TableCell>
                <TableCell>{Number(f.percentualConcluido).toFixed(2)}%</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" onClick={() => { setEditId(f.id); setNome(f.nome); setOrdem(String(f.ordem)); }}><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => remover(f.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  TAREFAS
// ════════════════════════════════════════════════════════════════════════════
function TarefasTab({ projetoId, usuarios }: { projetoId: number; usuarios: { id: number; nome: string }[] }) {
  const { data: tarefas = [] } = useTarefas(projetoId);
  const { data: fases = [] } = useFases(projetoId);
  const { salvarTarefa, excluirTarefa } = useProjetoSubMutations(projetoId);

  const [editId, setEditId] = useState<number | null>(null);
  const [nome, setNome] = useState("");
  const [faseId, setFaseId] = useState(SEM_FASE);
  const [responsavelId, setResponsavelId] = useState("0");
  const [esforco, setEsforco] = useState("0");
  const [pct, setPct] = useState("0");
  const [status, setStatus] = useState("0");

  const limpar = () => { setEditId(null); setNome(""); setFaseId(SEM_FASE); setResponsavelId("0"); setEsforco("0"); setPct("0"); setStatus("0"); };

  const salvar = async () => {
    if (!nome.trim()) { toast.error("Informe o nome da tarefa."); return; }
    try {
      await salvarTarefa.mutateAsync({
        tarefaId: editId ?? undefined,
        req: {
          nome: nome.trim(), faseId: Number(faseId), responsavelId: Number(responsavelId),
          esforcoEstimadoHoras: Number(esforco) || 0, percentualConcluido: Number(pct) || 0, status: Number(status),
        },
      });
      toast.success("Tarefa salva.");
      limpar();
    } catch (e) { toast.error(getApiErrorMessage(e, "Erro ao salvar tarefa.")); }
  };

  const remover = async (tid: number) => {
    try { await excluirTarefa.mutateAsync(tid); toast.success("Tarefa removida."); if (editId === tid) limpar(); }
    catch (e) { toast.error(getApiErrorMessage(e, "Erro ao remover tarefa.")); }
  };

  const nomeFase = (fid: number) => fases.find(f => f.id === fid)?.nome ?? "(sem fase)";
  const nomeResp = (rid: number) => usuarios.find(u => u.id === rid)?.nome ?? (rid ? `#${rid}` : "—");

  return (
    <Card>
      <CardHeader><CardTitle className="text-base">{editId ? "Editar tarefa" : "Nova tarefa"}</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <div className="md:col-span-2"><Label>Nome</Label><Input value={nome} onChange={e => setNome(e.target.value)} maxLength={160} /></div>
          <div>
            <Label>Fase</Label>
            <Select value={faseId} onValueChange={setFaseId}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={SEM_FASE}>(sem fase)</SelectItem>
                {fases.map(f => <SelectItem key={f.id} value={String(f.id)}>{f.nome}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Responsável</Label>
            <Select value={responsavelId} onValueChange={setResponsavelId}>
              <SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger>
              <SelectContent>
                <SelectItem value="0">— Nenhum —</SelectItem>
                {usuarios.map(u => <SelectItem key={u.id} value={String(u.id)}>{u.nome}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Esforço (h)</Label><Input type="number" step="0.01" value={esforco} onChange={e => setEsforco(e.target.value)} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>% Concluído</Label><Input type="number" step="0.01" value={pct} onChange={e => setPct(e.target.value)} /></div>
            <div>
              <Label>Status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUS_TAREFA.map((s, i) => <SelectItem key={i} value={String(i)}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <div className="flex gap-3">
          <Button onClick={salvar}><Save className="mr-2 h-4 w-4" />{editId ? "Atualizar" : "Adicionar"}</Button>
          {editId && <Button variant="outline" onClick={limpar}>Cancelar</Button>}
        </div>
        <Table>
          <TableHeader><TableRow><TableHead>Tarefa</TableHead><TableHead>Fase</TableHead><TableHead>Responsável</TableHead><TableHead>Status</TableHead><TableHead className="w-16">%</TableHead><TableHead className="w-24 text-right">Ações</TableHead></TableRow></TableHeader>
          <TableBody>
            {tarefas.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground">Nenhuma tarefa.</TableCell></TableRow>
            ) : tarefas.map(t => (
              <TableRow key={t.id}>
                <TableCell className="font-medium">{t.nome}</TableCell>
                <TableCell>{nomeFase(t.faseId)}</TableCell>
                <TableCell>{nomeResp(t.responsavelId)}</TableCell>
                <TableCell><Badge variant="outline">{STATUS_TAREFA[t.status] ?? t.status}</Badge></TableCell>
                <TableCell>{Number(t.percentualConcluido).toFixed(0)}%</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" onClick={() => {
                    setEditId(t.id); setNome(t.nome); setFaseId(String(t.faseId)); setResponsavelId(String(t.responsavelId));
                    setEsforco(String(t.esforcoEstimadoHoras)); setPct(String(t.percentualConcluido)); setStatus(String(t.status));
                  }}><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => remover(t.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  DEPENDÊNCIAS
// ════════════════════════════════════════════════════════════════════════════
function DependenciasTab({ projetoId }: { projetoId: number }) {
  const { data: tarefas = [] } = useTarefas(projetoId);
  const { data: deps = [] } = useDependencias(projetoId);
  const { adicionarDependencia, removerDependencia } = useProjetoSubMutations(projetoId);

  const [tarefaId, setTarefaId] = useState("");
  const [predId, setPredId] = useState("");
  const [tipo, setTipo] = useState("0");

  const nomeTarefa = (tid: number) => tarefas.find(t => t.id === tid)?.nome ?? `#${tid}`;

  const adicionar = async () => {
    if (!tarefaId || !predId) { toast.error("Selecione a tarefa e a predecessora."); return; }
    try {
      await adicionarDependencia.mutateAsync({ tarefaId: Number(tarefaId), tarefaPredecessoraId: Number(predId), tipo: Number(tipo) });
      toast.success("Dependência adicionada.");
      setTarefaId(""); setPredId("");
    } catch (e) { toast.error(getApiErrorMessage(e, "Não foi possível adicionar a dependência.")); }
  };

  const remover = async (depId: number) => {
    try { await removerDependencia.mutateAsync(depId); toast.success("Dependência removida."); }
    catch (e) { toast.error(getApiErrorMessage(e, "Erro ao remover dependência.")); }
  };

  return (
    <Card>
      <CardHeader><CardTitle className="text-base">Nova dependência</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        {tarefas.length < 2 && <p className="text-sm text-muted-foreground">Cadastre ao menos duas tarefas para criar dependências.</p>}
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[200px] flex-1">
            <Label>Tarefa</Label>
            <Select value={tarefaId} onValueChange={setTarefaId}>
              <SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger>
              <SelectContent>{tarefas.map(t => <SelectItem key={t.id} value={String(t.id)}>{t.nome}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="min-w-[200px] flex-1">
            <Label>Depende de</Label>
            <Select value={predId} onValueChange={setPredId}>
              <SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger>
              <SelectContent>{tarefas.map(t => <SelectItem key={t.id} value={String(t.id)}>{t.nome}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="min-w-[180px]">
            <Label>Tipo</Label>
            <Select value={tipo} onValueChange={setTipo}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{TIPO_DEPENDENCIA.map((s, i) => <SelectItem key={i} value={String(i)}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button onClick={adicionar}><Plus className="mr-2 h-4 w-4" />Adicionar</Button>
        </div>
        <Table>
          <TableHeader><TableRow><TableHead>Tarefa</TableHead><TableHead>Depende de</TableHead><TableHead>Tipo</TableHead><TableHead className="w-16 text-right">Ações</TableHead></TableRow></TableHeader>
          <TableBody>
            {deps.length === 0 ? (
              <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground">Nenhuma dependência.</TableCell></TableRow>
            ) : deps.map(d => (
              <TableRow key={d.id}>
                <TableCell className="font-medium">{nomeTarefa(d.tarefaId)}</TableCell>
                <TableCell>{nomeTarefa(d.tarefaPredecessoraId)}</TableCell>
                <TableCell>{TIPO_DEPENDENCIA[d.tipo] ?? d.tipo}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" onClick={() => remover(d.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
